// Recuperação de bundle stale: a aba roda o shell de uma versão anterior e um
// chunk lazy dessa versão não existe mais no edge (deploy novo publicado no
// meio da sessão). Sem tratamento, a tela quebra e só volta com F5 manual.
//
// O app se recupera sozinho — não há o que preservar quando o render já falhou.
// Um `location.reload()` cru não basta: com um service worker antigo no
// controle a navegação é servida pelo precache dele, o MESMO shell velho volta
// e o chunk falha de novo. Por isso a recarga ativa antes o SW novo.
//
// São no máximo duas tentativas automáticas por janela de 60s:
//   1ª — ativa o SW novo (skip waiting) e recarrega.
//   2ª — o reload anterior não resolveu: desregistra o SW, limpa os caches e
//        recarrega, forçando a navegação a ir à rede buscar o index.html novo.
//   3ª — devolve o controle à UI (toast / tela com botão), sem novo reload.
//
// O contador vive em sessionStorage porque precisa sobreviver ao reload; um
// boot que se manteve saudável por alguns segundos o zera
// (clearStaleBundleAttempts), então dois deploys na mesma sessão continuam
// tendo suas duas tentativas cada.

const ATTEMPTS_KEY = 'alamo:stale-bundle-attempts';
const ATTEMPTS_WINDOW_MS = 60_000;
const MAX_AUTO_ATTEMPTS = 2;
// Prazo para o SW novo instalar/assumir depois do update(). Estourado, recarrega
// mesmo assim (a falha provavelmente foi de rede, não de deploy).
const NEW_SW_TIMEOUT_MS = 4_000;
// Espera antes da 2ª tentativa. Sem ela as duas recargas se queimam em dezenas
// de milissegundos — o edge não teve tempo de servir o index.html novo e a
// tela de desistência aparece por um problema que teria passado sozinho.
const RETRY_DELAY_MS = 3_000;

const STALE_BUNDLE_PATTERNS: RegExp[] = [
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
  /expected a JavaScript(?:-or-Wasm)? module script/i,
  // O helper de preload do Vite entrega `undefined` no lugar do módulo quando o
  // evento `vite:preloadError` é cancelado; React.lazy então desreferencia
  // `undefined.default` e o TypeError chega ao error boundary sem qualquer
  // menção a chunk. Chrome e Safari formatam a mensagem de formas diferentes.
  /Cannot read propert(?:y|ies) of undefined \(reading '?default'?\)/i,
  /undefined is not an object \(evaluating '.*\.default'\)/i,
];

type Attempts = { count: number; at: number };

// Uma recuperação em andamento já vai recarregar a página: chamadas seguintes
// (outro chunk falhando, o error boundary montando) são no-op idempotentes.
let recovering = false;

function readAttempts(): Attempts {
  try {
    const raw = sessionStorage.getItem(ATTEMPTS_KEY);
    if (!raw) return { count: 0, at: 0 };
    const parsed = JSON.parse(raw) as Partial<Attempts>;
    const count = typeof parsed.count === 'number' ? parsed.count : 0;
    const at = typeof parsed.at === 'number' ? parsed.at : 0;
    // Ocorrência isolada fora da janela = provável deploy novo, não loop.
    if (Date.now() - at > ATTEMPTS_WINDOW_MS) return { count: 0, at: 0 };
    return { count, at };
  } catch {
    return { count: 0, at: 0 };
  }
}

function writeAttempts(attempts: Attempts): void {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(attempts));
  } catch {
    // Sem sessionStorage (modo privado antigo) o contador não sobrevive ao
    // reload; a flag de módulo ainda impede repetição dentro da mesma página.
  }
}

// Chamado quando o app já rodou tempo suficiente para se considerar saudável.
export function clearStaleBundleAttempts(): void {
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY);
  } catch {
    // storage indisponível — nada a limpar.
  }
}

export function isStaleBundleError(reason: unknown): boolean {
  if (reason instanceof Error) {
    return (
      reason.name === 'ChunkLoadError' ||
      STALE_BUNDLE_PATTERNS.some((pattern) => pattern.test(reason.message))
    );
  }
  if (typeof reason === 'string') {
    return STALE_BUNDLE_PATTERNS.some((pattern) => pattern.test(reason));
  }
  return false;
}

// Leitura pura: a UI usa para decidir o que pintar no primeiro render, antes de
// o efeito disparar a recuperação de fato.
export function canAutoRecover(): boolean {
  return recovering || readAttempts().count < MAX_AUTO_ATTEMPTS;
}

/**
 * Dispara a recarga na versão publicada. Retorna `false` quando as tentativas
 * automáticas da janela se esgotaram — aí cabe ao chamador oferecer o botão.
 */
export function recoverFromStaleBundle(): boolean {
  if (recovering) return true;

  const { count } = readAttempts();
  if (count >= MAX_AUTO_ATTEMPTS) return false;

  recovering = true;
  const attempt = count + 1;
  writeAttempts({ count: attempt, at: Date.now() });

  void runRecovery(attempt);
  return true;
}

async function runRecovery(attempt: number): Promise<void> {
  // A 2ª tentativa só acontece porque a 1ª (ativar o SW novo) não entregou o
  // shell novo — então dá tempo ao edge e sai da frente do service worker por
  // completo.
  if (attempt >= MAX_AUTO_ATTEMPTS) {
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    await unregisterServiceWorkers();
    await clearCaches();
  } else {
    await activateNewServiceWorker();
  }
  window.location.reload();
}

// Reseta o estado de módulo. Uso exclusivo de teste. `keepAttempts` simula o
// que o reload real faz: zera a flag de recuperação (contexto JS novo) mas
// preserva o contador, que vive em sessionStorage justamente para sobreviver.
export function __resetStaleBundleRecoveryForTest({ keepAttempts = false } = {}): void {
  recovering = false;
  if (!keepAttempts) clearStaleBundleAttempts();
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Pede um update ao registro e espera o SW novo assumir (controllerchange) ou
 * chegar a `waiting` — nesse caso manda SKIP_WAITING para promovê-lo. Resolve
 * no timeout se nenhum SW novo aparecer.
 */
async function activateNewServiceWorker(): Promise<void> {
  const registration = await getRegistration();
  // Sem SW no controle a navegação já vai à rede: o reload basta.
  if (!registration || !navigator.serviceWorker.controller) return;

  await new Promise<void>((resolve) => {
    let settled = false;
    const cleanups: Array<() => void> = [];

    function finish(): void {
      if (settled) return;
      settled = true;
      cleanups.forEach((fn) => fn());
      resolve();
    }

    function promoteWaiting(): void {
      registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    }

    function watchInstalling(): void {
      const installing = registration?.installing;
      if (!installing) return;
      const onStateChange = (): void => {
        if (installing.state === 'installed') promoteWaiting();
        if (installing.state === 'activated' || installing.state === 'redundant') finish();
      };
      installing.addEventListener('statechange', onStateChange);
      cleanups.push(() => installing.removeEventListener('statechange', onStateChange));
    }

    const onControllerChange = (): void => finish();
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    cleanups.push(() =>
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange),
    );

    const onUpdateFound = (): void => watchInstalling();
    registration.addEventListener('updatefound', onUpdateFound);
    cleanups.push(() => registration.removeEventListener('updatefound', onUpdateFound));

    const timer = setTimeout(finish, NEW_SW_TIMEOUT_MS);
    cleanups.push(() => clearTimeout(timer));

    // Um SW novo pode já estar instalado/instalando antes desta chamada.
    watchInstalling();
    promoteWaiting();

    void registration.update().catch(() => undefined);
  });
}

async function unregisterServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Sem permissão/suporte: o reload ainda pode resolver.
  }
}

async function clearCaches(): Promise<void> {
  if (!('caches' in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    // CacheStorage indisponível — segue para o reload.
  }
}
